<template>
  <UiModal
    title="Programare"
    :subtitle="`Pasul ${ booking.currentStep } din 3`"

    :buttons="
      booking.currentStep === 3
      ? [
        {
          icon: 'fas fa-arrow-circle-right',
          event: 'submit',
          style: 'gradient',
          text: booking.loading ? 'Procesare..' : 'Finalizează',
          disabled: booking.loading
        }
      ]
      : [
        {
          icon: 'fas fa-arrow-circle-right',
          event: 'nextStep',
          text: 'Următorul pas',
          disabled: (
            (booking.currentStep === 2 && booking.selectedStartTime === null)
          )
        }
      ]
    "

    @close="previousStep"
    @nextStep="nextStep"
    @submit="submit"

    v-if="booking.active && this.specialist.available"
  >
    <template v-slot:body>
      <div class="step-one" v-if="booking.currentStep === 1">
        <h2 class="booking-header">
          Selectează numărul de ședințe
        </h2>

        <div class="step-one__choose">
          <button class="step-one__choose__back" @click="decServicesNumber">
            <i class="fas fa-chevron-left" />
          </button>

          <div class="step-one__choose__appointments">
            {{ booking.servicesNumber }}
          </div>

          <button class="step-one__choose__next" @click="incServicesNumber">
            <i class="fas fa-chevron-right" />
          </button>
        </div>

        <h3 class="step-one__time">
          Timp total:
          <span>
            {{ metadata.standard_service_duration * booking.servicesNumber }} minute
          </span>
        </h3>

        <small class="booking-note" v-if="!(hasEnoughBalance)">
          * Alimentarea contului va fi necesară la finalizarea programării.
        </small>

        <!-- Keep this element for spacing -->
        <small class="booking-note" v-else />
      </div>

      <div class="step-two" v-else-if="booking.currentStep === 2">
        <template v-if="booking.availabilityDates === null">
          <h2 class="booking-header">
            Selectează o zi
          </h2>

          <div class="step-two__wrapper" style="overflow: hidden">
            <div class="step-two__day skeleton" style="border: none" />
            <div class="step-two__day skeleton" style="border: none" />
            <div class="step-two__day skeleton" style="border: none" />
            <div class="step-two__day skeleton" style="border: none" />
          </div>

          <h2 class="booking-header" style="margin-top: 25px">
            Selectează o oră
          </h2>

          <div class="step-two__wrapper" style="overflow: hidden">
            <div class="step-two__hour skeleton" style="border: none" />
            <div class="step-two__hour skeleton" style="border: none" />
            <div class="step-two__hour skeleton" style="border: none" />
            <div class="step-two__hour skeleton" style="border: none" />
            <div class="step-two__hour skeleton" style="border: none" />
            <div class="step-two__hour skeleton" style="border: none" />
          </div>
        </template>

        <p v-else-if="booking.availabilityDates.length === 0" style="margin-top: 30px">
          Nu au fost gasite zile disponibile.
        </p>

        <template v-else>
          <h2 class="booking-header">
            Selectează o zi
          </h2>

          <div class="arrowscroll-date-selector" v-arrowscroll>
            <div class="step-two__wrapper" v-dragscroll="!isMobile">
              <div
              class="step-two__day"
              v-for="(day, index) of booking.availabilityDates"
              :key="'day-' + index"
              :class="{
                'step-two__day--disabled': day.availabilitiesCount === 0,
                'step-two__day--selected': booking.selectedDayIndex === index,
              }"
              @click="selectDay(day, index)"
            >
              {{ new Date(day.date) | moment("D MMM YYYY") }}
              <span>
                {{ new Date(day.date) | moment("dddd") }}
              </span>
            </div>
            </div>
          </div>

          <h2 class="booking-header" style="margin-top: 25px">
            Selectează o oră
          </h2>

          <div class="arrowscroll-hour-selector" v-arrowscroll>
            <div class="step-two__wrapper" v-dragscroll="!isMobile" id="step-two-hours">
            <div
              class="step-two__hour"
              :class="{
                'step-two__hour--selected': booking.selectedStartTime === hour.start_timestamp,
              }"
              v-for="(hour, index) of hours"
              :key="'hour-' + index"
              @click="selectHour(hour)"
            >
              {{ new Date(hour.start_timestamp) | moment("HH:mm") }}
            </div>
          </div>
          </div>
        </template>
      </div>

      <div class="step-three" v-else-if="booking.currentStep === 3">
        <h2 class="booking-header">
          Rezumatul programării
        </h2>

        <div class="step-three__price">
          <div class="step-three__square" v-if="booking.summary.services > 0">
            {{ booking.summary.services }}
            <i class="fas fa-handshake" />
          </div>

          <div class="step-three__square" v-if="booking.summary.credits > 0">
            {{ booking.summary.credits }}
            <img src="@/assets/images/icons/coins.svg">
          </div>

          <div class="step-three__square step-three__square--money" v-if="booking.summary.money > 0">
            {{ booking.summary.money }}
            <span>RON</span>
          </div>
        </div>

        <div class="step-three__time">
          <div class="step-three__meta">
            <i class="fas fa-calendar" />
            <p class="step-three__meta__value" style="text-transform: capitalize;">
              {{ new Date(booking.availabilityDates[booking.selectedDayIndex].date) | moment("dddd, D MMMM YYYY") }}
            </p>
          </div>

          <div class="step-three__meta">
            <i class="fas fa-clock" />
            <p class="step-three__meta__value">
              {{ metadata.standard_service_duration * booking.servicesNumber }} minute
            </p>
          </div>
        </div>

        <small class="booking-note" v-if="!(hasEnoughBalance)">
          * Alimentarea contului va fi necesară la finalizarea programării.
        </small>
      </div>
    </template>
  </UiModal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import UiModal                    from "@/components/ui/Modal";

export default {
  components: { UiModal },
  mounted() {
    if (!(this.booking.specialistId)) {
      return this.modalClose();
    }

    if (!(this.$route.params.id)) {
      return this.modalClose();
    }

    if (this.booking.specialistId.toString() !== this.$route.params.id.toString()) {
      return this.modalClose();
    }

    if (this.$route.query && this.$route.query.booking.toString() === 'open') {
      this.modalOpen();
    }
  },
  computed: {
    ...mapGetters({
      booking      : "specialist/booking",
      metadata     : "specialist/metadata",
      specialist   : "specialist/specialist",
      user         : "user/user",
      userServices : "specialist/userServices",
      isMobile: 'layout/isMobile'
    }),

    specialistId() {
      return this.$route.params.id.toString();
    },

    hours() {
      if (this.booking.availabilityDates.length > this.booking.selectedDayIndex) {
        return this.booking.availabilityDates[this.booking.selectedDayIndex].availabilities;
      }

      return [];
    },

    hasEnoughBalance() {
      if (this.specialist && this.user) {
        if (
          (
            this.specialist.service_price * this.booking.servicesNumber
          ) > (
            this.user.balance + (this.userServices.length * this.specialist.service_price)
          )
        ) {
          return false;
        }
      }

      return true;
    },
  },
  methods: {
    ...mapActions({
      fetchAvailability : 'specialist/fetchAvailability',
      storeAppointment  : 'appointments/storeAppointment',
      billingDetailsModal: 'layout/billingDetailsModal'
    }),

    // Step One
    decServicesNumber() {
      if (this.booking.servicesNumber === this.specialist.min_services) {
        return;
      }

      this.$store.state.specialist.booking.servicesNumber--;
    },

    incServicesNumber() {
      if (this.booking.servicesNumber === 4) {
        return;
      }

      this.$store.state.specialist.booking.servicesNumber++;
    },

    // Step Two
    selectDay(day, index) {
      if (day.availabilitiesCount !== 0 && this.$store.state.specialist.booking.selectedDayIndex !== index) {
        this.$store.state.specialist.booking.selectedDayIndex = index;
        this.$store.state.specialist.booking.selectedStartTime = null;
        this.$store.state.specialist.booking.selectedEndTime = null;
      }
    },

    selectHour(hour) {
      this.$store.state.specialist.booking.selectedStartTime = hour.start_timestamp;
      this.$store.state.specialist.booking.selectedEndTime   = hour.end_timestamp;
    },

    calculateBookingCost() {
      let booking      = this.$store.state.specialist.booking;
      let totalPrice   = this.booking.servicesNumber * this.specialist.service_price;
      let userBalance  = this.user.balance;

      let available_services = 0;

      for(let i = 0; i < this.userServices.length; i++) {
        if (this.userServices[i].specialist_id.toString() === this.specialistId) {
          available_services++;
        }
      }

      if (available_services == booking.servicesNumber) {
        booking.summary.services = available_services;
      } else if (available_services > booking.servicesNumber) {
        booking.summary.services = booking.servicesNumber;
      } else {
        booking.summary.services = available_services;
      }

      let servicesDifference      = booking.servicesNumber - booking.summary.services;
      let priceDifferenceServices = servicesDifference * this.specialist.service_price;

      if (userBalance >= priceDifferenceServices) {
        booking.summary.credits = priceDifferenceServices;
        priceDifferenceServices = 0;
      } else {
        booking.summary.credits  = userBalance;
        priceDifferenceServices -= userBalance;
      }

      booking.summary.money = priceDifferenceServices;
    },
    // All
    modalClose() {
      this.$store.state.specialist.booking.active = false;
      this.$store.state.specialist.booking.currentStep = 1;
      this.$store.state.specialist.booking.selectedDayIndex = 0;
      this.$store.state.specialist.booking.selectedStartTime = null;
      this.$store.state.specialist.booking.selectedEndTime = null;
    },

    modalOpen() {
      this.$store.state.specialist.booking.servicesNumber = this.specialist.min_services;

      this.$store.state.specialist.booking.active = true;
      this.$store.state.specialist.booking.specialistId = this.specialistId;
    },

    nextStep() {
      if (this.booking.currentStep < 3) {
        this.$store.state.specialist.booking.currentStep++;
      }

      if (this.booking.currentStep === 2) {
        this.fetchAvailability({
          specialist_id: this.booking.specialistId,
          services_count: this.booking.servicesNumber
        });
      }

      if (this.booking.currentStep === 3) {
        this.calculateBookingCost();
      }
    },

    previousStep(event) {
      if(event)
        if (event.target.tagName === 'I') {
          if (this.booking.currentStep === 1) {
            return this.modalClose();
          }

          return this.$store.state.specialist.booking.currentStep--;
        }
      else this.modalClose();
    },

    submit() {

      if(localStorage.getItem('address') !== 'true'){
        this.billingDetailsModal(true);
        return;
      }

      if (this.booking.loading) {
        return;
      }

      this.$store.state.specialist.booking.loading = true;

      this.storeAppointment({
        start_time   : this.booking.selectedStartTime,
        specialistId : this.specialistId,
        services     : this.booking.servicesNumber,
        credits      : this.booking.summary.credits
      });
    }
  },
  watch: {
    'specialist'(specialist) {
      this.$store.state.specialist.booking.servicesNumber = specialist.min_services;
    }
  }
};
</script>
<style lang="scss" scoped>
.booking-header {
  font-size: 18px;
  font-weight: 500;
  line-height: 1;
  margin-top: 30px;
}

.booking-note {
  color: rgba(#373435, 0.7);
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  margin-top: 20px;
  text-align: center;
}

.step-one {
  text-align: center;
  padding-bottom: 20px;

  &__choose {
    align-items: center;
    display: flex;
    justify-content: center;
    width: 100%;

    &__appointments {
      color: $color-secondary;
      font-family: 'Roboto';
      font-size: 140px;
      font-weight: 700;
      line-height: 1;
      padding-left: 20px;
      padding-right: 20px;
      user-select: none;
    }

    &__back,
    &__next {
      background: rgba(#373435, 25%);
      border: none;
      border-radius: 9999px;
      cursor: pointer;
      height: 35px;
      outline: none;
      width: 35px;

      &:hover {
        background: rgba(#373435, 40%);
      }

      & > i {
        color: #FFFFFF;
        font-size: 20px;
      }
    }
  }

  &__time {
    font-size: 16px;
    font-weight: 500;
    line-height: 1;
    margin-top: 10px;
    text-align: center;

    & > span {
      color: $color-secondary;
    }
  }
}

.step-two {
  padding-bottom: 50px;

  &__wrapper {
    width: 100%;
    margin-top: 15px;
    display: flex;
    overflow-x: auto;
    cursor: grab;
    padding-bottom: 1px;

    &:active{
      cursor: grabbing;
    }
  }

  &__day {
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
    height: 100px;
    margin-right: 20px;
    min-width: 100px;
  }

  &__hour {
    font-weight: 500;
    line-height: 1;
    text-align: center;
    height: 30px;
    margin-right: 15px;
    min-width: 70px;
  }

  &__day,
  &__hour {
    align-items: center;
    border-image: $gradient-nexyo;
    border-style: solid;
    border-width: 3px;
    border-image-slice: 1;
    cursor: pointer;
    color: $color-primary;
    display: flex;
    font-size: 16px;
    flex-direction: column;
    justify-content: center;
    text-transform: capitalize;

    & > span {
      display: block;
      text-transform: capitalize;
    }

    &--disabled {
      background: rgba(55, 52, 53, 0.25);
      border-image: none;
      border-color: transparent;
      color: #FFFFFF;
      display: none;
    }

    &:not(&--disabled):hover {
      opacity: 0.95;
    }

    &--selected:not(&--disabled) {
      background: $gradient-nexyo;
      color: #FFFFFF;
    }
  }
}

.step-three {
  padding-bottom: 20px;

  &__price {
    display: flex;
    width: 95%;
    margin: auto;
  }

  &__square {
    align-items: center;
    background: $color-primary;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.24);
    border-radius: 5px;
    color: #FFFFFF;
    display: flex;
    justify-content: center;
    height: 70px;
    width: 70px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    margin-right: 30px;
    margin-top: 15px;

    &:last-child {
      margin-right: 0;
    }

    &--money {
      background: $color-secondary;
      flex-direction: column;
    }

    & > i,
    & > img {
      margin-left: 5px;
    }
  }

  &__time {
    background: #FFFFFF;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.06);
    width: 95%;
    display: flex;
    flex-direction: column;
    margin: 20px auto 0px auto;
  }

  &__meta {
    align-items: center;
    display: flex;
    padding: 10px;

    &:first-child {
      padding-bottom: 5px;
    }

    & > i {
      font-size: 20px;
      width: 30px;
      text-align: center;
      margin-right: 5px;
      color: $color-secondary;
    }

    &__value {
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
    }
  }
}
</style>